import {
    ArrowDownIcon,
    ArrowUpIcon,
    CaretSortIcon,
    DragHandleVerticalIcon,
} from '@radix-ui/react-icons';
import {
    Button,
    DropdownMenu,
    Flex,
    FlexProps,
    Table,
    Text,
} from '@radix-ui/themes';
import { Column, flexRender, Header } from '@tanstack/react-table';
import * as React from 'react';

import classNames from 'classnames';
import { TruncatedTooltip } from '../../../common/components/TruncatedTooltip';
import styles from './TableDisplay.module.css';
import { useMeasure } from '@common/hooks/useMeasure';
interface TableColumnHeaderProps<TData, TValue>
    extends React.HTMLAttributes<HTMLDivElement> {
    header: Header<TData, TValue>;
    isSortingEnabled: boolean;
    isResizingEnabled: boolean;
}

const ICONS_BY_SORT_ORDER = {
    asc: ArrowUpIcon,
    desc: ArrowDownIcon,
    false: CaretSortIcon,
};

const getSortIcon = <TData, TValue>(column: Column<TData, TValue>) => {
    const isSorted = column.getIsSorted();

    return ICONS_BY_SORT_ORDER[
        String(isSorted) as keyof typeof ICONS_BY_SORT_ORDER
    ];
};

const Layout = ({ children, ...rest }: FlexProps) => {
    return (
        <Flex
            width={'100%'}
            height={'100%'}
            align={'center'}
            justify={'start'}
            gap={'1'}
            overflow={'visible'}
            {...rest}
        >
            {children}
        </Flex>
    );
};

export const TableColumnHeader = <TData, TValue>({
    header,
    className,
    style,
    isSortingEnabled,
    isResizingEnabled,
}: TableColumnHeaderProps<TData, TValue>) => {
    const [headerRef, { width: observedWidth }] =
        useMeasure<HTMLTableCellElement>();

    const column = header.column;
    const showSortControls = isSortingEnabled && column.getCanSort();
    const noWrapStyle: React.CSSProperties = {
        whiteSpace: 'nowrap',
        overflow: 'ellipsis',
    };

    const availableTextWidth = observedWidth
        ? `${observedWidth - (showSortControls ? 32 : 16)}px`
        : '100%';

    const Tag = (
        <Text
            weight={'bold'}
            size={'2'}
            color='gray'
            wrap={'nowrap'}
            highContrast
            style={{ width: '100%' }}
            asChild
        >
            <TruncatedTooltip
                maxWidth={availableTextWidth}
                enabled={header.id !== 'select'}
            >
                {flexRender(column.columnDef.header, header.getContext())}
            </TruncatedTooltip>
        </Text>
    );
    const SortIcon = getSortIcon(column);

    return (
        <Table.ColumnHeaderCell
            ref={headerRef}
            className={styles.headerCell}
            colSpan={header.colSpan}
            style={{
                width: header.getSize(),
            }}
        >
            {showSortControls ? (
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger
                        className={className}
                        style={{ ...style, ...noWrapStyle }}
                    >
                        <Button
                            style={{
                                ...style,
                                ...noWrapStyle,
                                minWidth: 'fit-content',
                            }}
                            color='gray'
                            highContrast
                            variant='ghost'
                            asChild
                        >
                            <Layout>
                                <SortIcon
                                    width={'21'}
                                    height={'21'}
                                    style={{ flexShrink: 0 }}
                                />

                                {Tag}
                            </Layout>
                        </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align='start'>
                        <DropdownMenu.Item
                            onClick={() => column.toggleSorting(false)}
                        >
                            <ArrowUpIcon />
                            Asc
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            onClick={() => column.toggleSorting(true)}
                        >
                            <ArrowDownIcon />
                            Desc
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu.Root>
            ) : (
                <Layout
                    className={className}
                    style={{ ...style, ...noWrapStyle }}
                >
                    {Tag}
                </Layout>
            )}

            {isResizingEnabled && header.column.getCanResize() && (
                <Flex
                    align={'center'}
                    justify={'center'}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className={classNames(
                        styles.resizer,
                        header.column.getIsResizing() && styles.isResizing,
                    )}
                >
                    <DragHandleVerticalIcon />
                </Flex>
            )}
        </Table.ColumnHeaderCell>
    );
};
